<script lang="ts">
  export interface DonutChartSegment {
    label: string;
    value: number;
  }

  const CHART_COLOR_TOKENS = [
    'var(--chart-1)',
    'var(--chart-2)',
    'var(--chart-3)',
    'var(--chart-4)',
    'var(--chart-5)',
  ];

  const EMPTY_SEGMENT_COLOR = 'var(--color-gray-02)';

  const ANIMATION_DURATION_MS = 600;

  const valueFormatter = new Intl.NumberFormat('pt-BR');
  const percentFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  const HTML_ESCAPES: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
  }
</script>

<script setup lang="ts">
  import { computed } from 'vue';
  import { Donut } from '@unovis/ts';
  import { VisDonut, VisSingleContainer, VisTooltip } from '@unovis/vue';
  import { usePrefersReducedMotion } from '@/shared/lib';

  interface DonutArc {
    data: DonutChartSegment;
  }

  interface Props {
    segments: DonutChartSegment[];
    centerValue: string;
    centerLabel?: string;
    size?: number;
    arcWidth?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    centerLabel: undefined,
    size: 192,
    arcWidth: undefined,
  });

  const prefersReducedMotion = usePrefersReducedMotion();
  const animationDuration = computed(() => (prefersReducedMotion.value ? 0 : ANIMATION_DURATION_MS));

  const total = computed(() => props.segments.reduce((sum, segment) => sum + segment.value, 0));
  const isEmpty = computed(() => total.value <= 0);

  const chartData = computed<DonutChartSegment[]>(() =>
    isEmpty.value ? [{ label: '', value: 1 }] : props.segments
  );

  const ringWidth = computed(() => props.arcWidth ?? props.size * 0.1875);

  const legendItems = computed(() =>
    props.segments.map((segment, index) => ({
      label: segment.label,
      color: CHART_COLOR_TOKENS[index % CHART_COLOR_TOKENS.length],
    }))
  );

  const segmentValue = (segment: DonutChartSegment) => segment.value;

  const segmentColor = (_segment: DonutChartSegment, index: number) =>
    isEmpty.value ? EMPTY_SEGMENT_COLOR : CHART_COLOR_TOKENS[index % CHART_COLOR_TOKENS.length];

  function formatTooltip(segment: DonutChartSegment): string {
    const percent = percentFormatter.format(segment.value / total.value);
    return `${escapeHtml(segment.label)}: ${valueFormatter.format(segment.value)} (${percent})`;
  }

  const tooltipTriggers = {
    [Donut.selectors.segment]: (arc: DonutArc) => (isEmpty.value ? null : formatTooltip(arc.data)),
  };

  const chartDescription = computed(() => {
    const center = [props.centerValue, props.centerLabel].filter(Boolean).join(' ');
    if (isEmpty.value) return center;
    const segmentDescriptions = props.segments.map((segment) => {
      const percent = percentFormatter.format(segment.value / total.value);
      return `${segment.label}: ${valueFormatter.format(segment.value)} (${percent})`;
    });
    return `${center}. ${segmentDescriptions.join('; ')}`;
  });
</script>

<template>
  <div
    data-testid="donut-chart"
    class="flex flex-col items-center gap-1"
  >
    <div
      role="img"
      :aria-label="chartDescription"
      class="relative"
      :style="{ width: `${props.size}px`, height: `${props.size}px` }"
    >
      <VisSingleContainer
        :data="chartData"
        :width="props.size"
        :height="props.size"
      >
        <VisTooltip :triggers="tooltipTriggers" />
        <VisDonut
          :value="segmentValue"
          :color="segmentColor"
          :arc-width="ringWidth"
          :duration="animationDuration"
          :show-background="false"
        />
      </VisSingleContainer>
      <div
        class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center"
      >
        <p
          data-testid="donut-chart-center-value"
          class="text-3xl/9.5 font-bold text-gray-07"
        >
          {{ props.centerValue }}
        </p>
        <p
          v-if="props.centerLabel"
          data-testid="donut-chart-center-label"
          class="max-w-24 text-[10px]/3.5 text-gray-03"
        >
          {{ props.centerLabel }}
        </p>
      </div>
    </div>
    <ul class="flex items-center gap-4">
      <li
        v-for="item in legendItems"
        :key="item.label"
        data-testid="donut-chart-legend-item"
        class="flex items-center gap-2 p-0.5"
      >
        <span
          data-testid="donut-chart-legend-dot"
          class="size-3 rounded-full"
          :style="{ backgroundColor: item.color }"
        />
        <span class="text-[10px]/5 text-gray-03">{{ item.label }}</span>
      </li>
    </ul>
  </div>
</template>
